import React, { useState, useMemo } from 'react';
import calendarStyles, { statusColors } from './calendarStyles';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const LANE_HEIGHT = 22;
const LANE_GAP = 2;
const DAY_NUMBER_HEIGHT = 26;

// Build array of Date objects for the full calendar grid (padded to full weeks)
function getCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - startDate.getDay());

  const endDate = new Date(lastDay);
  endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

  const days = [];
  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Assign each reservation a lane index so overlapping bars stack
function assignLanes(reservations, parseDateLocal) {
  const sorted = [...reservations].sort((a, b) => {
    const diff = parseDateLocal(a['CHECK-IN']) - parseDateLocal(b['CHECK-IN']);
    if (diff !== 0) return diff;
    const durA = parseDateLocal(a['CHECK-OUT']) - parseDateLocal(a['CHECK-IN']);
    const durB = parseDateLocal(b['CHECK-OUT']) - parseDateLocal(b['CHECK-IN']);
    return durB - durA;
  });

  const lanes = [];
  const laneMap = new Map();

  for (const res of sorted) {
    const start = parseDateLocal(res['CHECK-IN']);
    const end = parseDateLocal(res['CHECK-OUT']);

    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      if (lanes[i] <= start) {
        lanes[i] = end;
        laneMap.set(res.UID, i);
        placed = true;
        break;
      }
    }
    if (!placed) {
      laneMap.set(res.UID, lanes.length);
      lanes.push(end);
    }
  }

  return { laneMap, totalLanes: lanes.length };
}

// Get reservations that overlap a given day (check-in <= day <= check-out)
// Includes check-out day for partial bar rendering
function getOverlapping(day, reservations, parseDateLocal) {
  return reservations.filter(r => {
    const ci = parseDateLocal(r['CHECK-IN']);
    const co = parseDateLocal(r['CHECK-OUT']);
    return day >= ci && day <= co;
  });
}

// Compute the date range that has reservation coverage (first check-in to last check-out)
function getReservationSpan(reservations, parseDateLocal) {
  if (!reservations.length) return { earliest: null, latest: null };
  let earliest = null;
  let latest = null;
  for (const r of reservations) {
    const ci = parseDateLocal(r['CHECK-IN']);
    const co = parseDateLocal(r['CHECK-OUT']);
    if (!earliest || ci < earliest) earliest = ci;
    if (!latest || co > latest) latest = co;
  }
  return { earliest, latest };
}

// Convert "HH:MM" time string to percentage of day (0-100)
function timeToPercent(timeStr) {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(':').map(Number);
  return ((h * 60 + (m || 0)) / 1440) * 100;
}

// Format hours between two times for cleaning window display
function cleaningWindowHours(checkOutTime, checkInTime) {
  if (!checkOutTime || !checkInTime) return null;
  const [coH, coM] = checkOutTime.split(':').map(Number);
  const [ciH, ciM] = checkInTime.split(':').map(Number);
  const coMinutes = coH * 60 + (coM || 0);
  const ciMinutes = ciH * 60 + (ciM || 0);
  const diff = ciMinutes - coMinutes;
  if (diff <= 0) return null;
  const hours = Math.floor(diff / 60);
  const mins = diff % 60;
  return mins > 0 ? `${hours}h${mins}m` : `${hours}h`;
}

const CalendarMonthGrid = ({
  reservations,
  getReservationStatus,
  parseDateLocal,
  onReservationClick,
  onEmptyDayClick,
  checkInTime,   // property check-in time "HH:MM" (e.g. "17:00")
  checkOutTime,  // property check-out time "HH:MM" (e.g. "11:00")
}) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [hoveredUID, setHoveredUID] = useState(null);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const days = useMemo(() => getCalendarDays(year, month), [year, month]);

  const allReservations = useMemo(() => reservations || [], [reservations]);

  const { laneMap, totalLanes } = useMemo(
    () => assignLanes(allReservations, parseDateLocal),
    [allReservations, parseDateLocal]
  );

  // Only highlight gaps between the earliest check-in and latest check-out
  const { earliest, latest } = useMemo(
    () => getReservationSpan(allReservations, parseDateLocal),
    [allReservations, parseDateLocal]
  );

  // Compute reservation bar segments for grid-spanning overlay rendering.
  // Each reservation is split into per-week-row segments so bars span
  // multiple columns as a single continuous block.
  const reservationSegments = useMemo(() => {
    const segments = [];
    const ciPercent = timeToPercent(checkInTime || '15:00');
    const coPercent = timeToPercent(checkOutTime || '11:00');

    for (const res of allReservations) {
      const ci = parseDateLocal(res['CHECK-IN']);
      const co = parseDateLocal(res['CHECK-OUT']);
      const lane = laneMap.get(res.UID) || 0;
      const status = getReservationStatus(res['CHECK-IN'], res['CHECK-OUT'], res.reservation_type);
      const colors = statusColors[status] || statusColors.INACTIVE;
      const label = res.assigned_cleaner && res.assigned_cleaner !== 'Unassigned'
        ? res.assigned_cleaner.split(' ')[0]
        : 'Unassigned';

      let startIdx = days.findIndex(d => isSameDay(d, ci));
      let endIdx = days.findIndex(d => isSameDay(d, co));

      // Clamp to visible range
      if (startIdx === -1) {
        if (ci < days[0]) startIdx = 0;
        else continue;
      }
      if (endIdx === -1) {
        if (co > days[days.length - 1]) endIdx = days.length - 1;
        else continue;
      }

      const isActualStart = isSameDay(days[startIdx], ci);
      const isActualEnd = isSameDay(days[endIdx], co);

      // Split into per-week-row segments, then only label the widest one
      const resSegments = [];
      let segStart = startIdx;
      while (segStart <= endIdx) {
        const row = Math.floor(segStart / 7);
        const rowEnd = (row + 1) * 7 - 1;
        const segEnd = Math.min(endIdx, rowEnd);
        const startCol = segStart % 7;
        const endCol = segEnd % 7;
        const numCols = endCol - startCol + 1;
        const isStart = segStart === startIdx && isActualStart;
        const isEnd = segEnd === endIdx && isActualEnd;

        resSegments.push({
          res,
          lane,
          colors,
          label,
          status,
          startCol,
          endCol,
          row,
          numCols,
          isStart,
          isEnd,
          barLeft: isStart ? `${ciPercent / numCols}%` : '0',
          barRight: isEnd ? `${(100 - coPercent) / numCols}%` : '0',
          barRadius: isStart && isEnd ? '4px'
            : isStart ? '4px 0 0 4px'
            : isEnd ? '0 4px 4px 0'
            : '0',
          title: `${res.assigned_cleaner || 'Unassigned'} | ${status} | ${res['CHECK-IN']} to ${res['CHECK-OUT']}`,
        });

        segStart = segEnd + 1;
      }

      // Only show label on the widest segment
      let widestIdx = 0;
      for (let i = 1; i < resSegments.length; i++) {
        if (resSegments[i].numCols > resSegments[widestIdx].numCols) widestIdx = i;
      }
      for (let i = 0; i < resSegments.length; i++) {
        resSegments[i].showLabel = (i === widestIdx);
      }
      segments.push(...resSegments);
    }

    return segments;
  }, [allReservations, days, laneMap, getReservationStatus, parseDateLocal, checkInTime, checkOutTime]);

  const navigateMonth = (delta) => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  const goToToday = () => {
    const now = new Date();
    setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  const monthLabel = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
  const cellMinHeight = DAY_NUMBER_HEIGHT + Math.max(totalLanes, 1) * (LANE_HEIGHT + LANE_GAP) + 6;

  return (
    <div>
      {/* Legend */}
      <div style={calendarStyles.legend}>
        {Object.entries(statusColors).map(([status, colors]) => (
          <div key={status} style={calendarStyles.legendItem}>
            <div style={{ ...calendarStyles.legendSwatch, backgroundColor: colors.bg, border: `1px solid ${colors.border}` }} />
            <span>{status.charAt(0) + status.slice(1).toLowerCase()}</span>
          </div>
        ))}
        <div style={calendarStyles.legendItem}>
          <div style={{ ...calendarStyles.legendSwatch, backgroundColor: 'var(--bg-gap)', border: '1px solid var(--border-gap)' }} />
          <span>Gap</span>
        </div>
      </div>

      {/* Navigation */}
      <div style={calendarStyles.navBar}>
        <div style={calendarStyles.navLeft}>
          <button
            onClick={() => navigateMonth(-1)}
            style={calendarStyles.navButton}
            onMouseOver={e => { e.currentTarget.style.backgroundColor = 'var(--bg-button-secondary-hover)'; }}
            onMouseOut={e => { e.currentTarget.style.backgroundColor = 'var(--bg-button-secondary)'; }}
          >
            &larr; Prev
          </button>
          <button
            onClick={goToToday}
            style={calendarStyles.todayButton}
            onMouseOver={e => { e.currentTarget.style.backgroundColor = 'var(--color-primary-hover)'; }}
            onMouseOut={e => { e.currentTarget.style.backgroundColor = 'var(--color-primary)'; }}
          >
            Today
          </button>
          <button
            onClick={() => navigateMonth(1)}
            style={calendarStyles.navButton}
            onMouseOver={e => { e.currentTarget.style.backgroundColor = 'var(--bg-button-secondary-hover)'; }}
            onMouseOut={e => { e.currentTarget.style.backgroundColor = 'var(--bg-button-secondary)'; }}
          >
            Next &rarr;
          </button>
        </div>
        <h3 style={calendarStyles.monthLabel}>{monthLabel}</h3>
        <div style={calendarStyles.navRight} />
      </div>

      {/* Calendar card */}
      <div style={calendarStyles.calendarCard}>
        <div style={calendarStyles.grid}>
          {/* Weekday headers — explicit grid positions */}
          {WEEKDAYS.map((day, i) => (
            <div key={day} style={{ ...calendarStyles.weekdayCell, gridColumn: i + 1, gridRow: 1 }}>{day}</div>
          ))}

          {/* Day cells — explicit grid positions, no reservation bars inside */}
          {days.map((day, idx) => {
            const isCurrentMonth = day.getMonth() === month;
            const isToday = isSameDay(day, today);
            const overlapping = isCurrentMonth ? getOverlapping(day, allReservations, parseDateLocal) : [];
            const isOccupied = overlapping.length > 0;

            // Only mark as gap if the day is between the earliest and latest reservation dates
            const isGap = isCurrentMonth && !isOccupied && earliest && latest &&
              day >= earliest && day < latest;

            const isLastCol = (idx % 7) === 6;

            const cellStyle = {
              ...(isCurrentMonth ? calendarStyles.dayCell : calendarStyles.dayCellOutside),
              ...(isGap ? calendarStyles.dayCellEmpty : {}),
              minHeight: `${cellMinHeight}px`,
              ...(isLastCol ? { borderRight: 'none' } : {}),
              gridColumn: (idx % 7) + 1,
              gridRow: Math.floor(idx / 7) + 2,
            };

            const numberStyle = !isCurrentMonth
              ? calendarStyles.dayNumberOutside
              : isToday
                ? calendarStyles.dayNumberToday
                : calendarStyles.dayNumber;

            return (
              <div
                key={idx}
                style={cellStyle}
                onClick={() => {
                  if (isCurrentMonth && !isOccupied && onEmptyDayClick) {
                    onEmptyDayClick(formatDate(day));
                  }
                }}
                onMouseOver={e => {
                  if (isCurrentMonth && isGap) {
                    e.currentTarget.style.backgroundColor = 'var(--bg-gap-hover)';
                  } else if (isCurrentMonth && !isOccupied) {
                    e.currentTarget.style.backgroundColor = 'var(--bg-card-hover)';
                  }
                }}
                onMouseOut={e => {
                  if (!isCurrentMonth) {
                    e.currentTarget.style.backgroundColor = 'var(--bg-table-header)';
                  } else if (isGap) {
                    e.currentTarget.style.backgroundColor = 'var(--bg-gap)';
                  } else {
                    e.currentTarget.style.backgroundColor = 'var(--bg-card)';
                  }
                }}
              >
                <span style={numberStyle}>{day.getDate()}</span>

                {/* Cleaning window indicator on checkout day */}
                {(() => {
                  // Check if there's a gap between checkout and next check-in on same day
                  const coRes = overlapping.find(r => isSameDay(day, parseDateLocal(r['CHECK-OUT'])));
                  const ciRes = overlapping.find(r => isSameDay(day, parseDateLocal(r['CHECK-IN'])) && (!coRes || r.UID !== coRes.UID));
                  if (coRes && ciRes && checkOutTime && checkInTime) {
                    const windowLabel = cleaningWindowHours(checkOutTime, checkInTime);
                    if (windowLabel) {
                      const coP = timeToPercent(checkOutTime);
                      const ciP = timeToPercent(checkInTime);
                      return (
                        <div
                          title={`Cleaning window: ${checkOutTime} – ${checkInTime} (${windowLabel})`}
                          style={{
                            position: 'absolute',
                            top: `${DAY_NUMBER_HEIGHT}px`,
                            left: `${coP}%`,
                            width: `${ciP - coP}%`,
                            height: `${LANE_HEIGHT}px`,
                            backgroundColor: 'var(--bg-gap, #fef9c3)',
                            borderTop: '1px dashed #eab30888',
                            borderBottom: '1px dashed #eab30888',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '8px',
                            fontWeight: '600',
                            color: '#92400e',
                            zIndex: 0,
                            pointerEvents: 'none',
                          }}
                        >
                          {windowLabel}
                        </div>
                      );
                    }
                  }
                  return null;
                })()}
              </div>
            );
          })}

          {/* Reservation bars — single continuous block spanning all days of the stay */}
          {reservationSegments.map((seg, i) => (
            <div
              key={`seg-${i}`}
              style={{
                gridColumn: `${seg.startCol + 1} / ${seg.endCol + 2}`,
                gridRow: seg.row + 2,
                position: 'relative',
                pointerEvents: 'none',
                zIndex: 2,
              }}
            >
              <div
                title={seg.title}
                onClick={e => {
                  e.stopPropagation();
                  if (onReservationClick) onReservationClick(seg.res);
                }}
                style={{
                  position: 'absolute',
                  top: `${DAY_NUMBER_HEIGHT + seg.lane * (LANE_HEIGHT + LANE_GAP)}px`,
                  left: seg.barLeft,
                  right: seg.barRight,
                  height: `${LANE_HEIGHT}px`,
                  backgroundColor: seg.colors.bg,
                  color: seg.colors.text,
                  fontSize: '10px',
                  fontWeight: '700',
                  lineHeight: `${LANE_HEIGHT}px`,
                  textAlign: 'center',
                  borderRadius: seg.barRadius,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  pointerEvents: 'auto',
                  borderLeft: seg.isStart ? `3px solid ${seg.colors.border}` : 'none',
                  borderTop: `1px solid ${seg.colors.border}44`,
                  borderBottom: `1px solid ${seg.colors.border}44`,
                  borderRight: seg.isEnd ? `1px solid ${seg.colors.border}44` : 'none',
                  filter: hoveredUID === seg.res.UID ? 'brightness(0.92)' : 'none',
                  boxShadow: hoveredUID === seg.res.UID ? '0 1px 4px rgba(0,0,0,0.18)' : 'none',
                  transition: 'filter 0.1s, box-shadow 0.1s',
                }}
                onMouseOver={() => setHoveredUID(seg.res.UID)}
                onMouseOut={() => setHoveredUID(null)}
              >
                {seg.showLabel ? seg.label : ''}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CalendarMonthGrid;
